import { NextPage } from 'next'
import Link from 'next/link'
import React, { useEffect } from 'react'

import { compose } from '@reduxjs/toolkit'
import { FormattedMessage, useIntl } from 'react-intl'
import { connect } from 'react-redux'

import { Button, Grid } from '@material-ui/core'

import ReferralsCard from '~/components/cards/ReferralsCard/ReferralsCard'
import AppLayout from '~/components/layouts/AppLayout'
import { Flexbox, Title, Loader } from '~/components/shared'
import Center from '~/containers/Center'
import Clinic from '~/containers/Clinic'

import { getDashboards } from '~/state/modules/dashboard/dashboard.action'
import { DashboardData } from '~/state/modules/dashboard/dashboard.slice'
import { AppState } from '~/state/store'
import withAuth, { Auth } from '~/utils/withAuth'

import ROUTES from '~/constants/routes'
import { STATUSES } from '~/constants/statuses'
import msg from '~/i18n/messages/pages/dashboard.messages'
import { useDashboardStyle } from '~/styles/dashboard.styles'

type ReduxProps = {
  role: RolesUnion
  data: Partial<DashboardData>
  loading: boolean
}

type DispatchProps = {
  getDashboards: () => void
}

type AllProps = ReduxProps & DispatchProps

const Dashboard: NextPage<AllProps> = ({
  role,
  loading,
  data: {
    clinics_collection,
    completed_referrals,
    new_referrals,
    scheduled_referrals
  },

  getDashboards
}) => {
  const s = useDashboardStyle()

  const { formatMessage } = useIntl()

  useEffect(() => {
    if (role) {
      getDashboards()
    }
  }, [role])

  return (
    <AppLayout>
      <Grid container spacing={2} justify="center">
        <Grid item xs={12}>
          <Flexbox className={s.container}>
            <Title>
              <FormattedMessage {...msg.dashboardTitle} />
            </Title>

            <Link href={ROUTES.addReferral} passHref>
              <Button variant="contained" color="primary" className={s.btn}>
                <FormattedMessage {...msg.addReferralBtn} />
              </Button>
            </Link>
          </Flexbox>
        </Grid>

        {loading || !role ? (
          <Loader />
        ) : (
          <>
            <Clinic>
              <>
                <Grid item xs={12} md={6}>
                  <ReferralsCard
                    title={formatMessage(msg.readyReferrals)}
                    countReferrals={completed_referrals}
                    status={STATUSES.completed}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <ReferralsCard
                    title={formatMessage(msg.pendingReferrals)}
                    countReferrals={new_referrals}
                    status={STATUSES.new}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <ReferralsCard
                    title={formatMessage(msg.scheduledReferrals)}
                    countReferrals={scheduled_referrals}
                    status={STATUSES.scheduled}
                  />
                </Grid>
              </>
            </Clinic>

            <Center>
              <>
                <Grid item xs={12} md={6}>
                  <ReferralsCard
                    title={formatMessage(msg.completedReferrals)}
                    countReferrals={completed_referrals}
                    status={STATUSES.completed}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <ReferralsCard
                    title={formatMessage(msg.clinics)}
                    countReferrals={clinics_collection}
                    isClinic
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <ReferralsCard
                    title={formatMessage(msg.newReferrals)}
                    countReferrals={new_referrals}
                    status={STATUSES.new}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <ReferralsCard
                    title={formatMessage(msg.scheduledReferrals)}
                    countReferrals={scheduled_referrals}
                    status={STATUSES.scheduled}
                  />
                </Grid>
              </>
            </Center>
          </>
        )}
      </Grid>
    </AppLayout>
  )
}

const mapStateToProps = ({
  user: { role },
  dashboard: { data, loading }
}: AppState): ReduxProps => {
  return {
    role,
    data,
    loading
  }
}

const withConnect = connect(mapStateToProps, { getDashboards })

export default compose<React.FC>(
  withAuth({ auth: Auth.Authorized }),
  withConnect
)(Dashboard)
